import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import CheckoutForm from "./donation-checkout-form";
import ArrowLeftIcon from "./assets/arrow-left-icon";
import Router from "next/router";

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string,
);

const MIN_DONATION_CENTS = 100;
const DEFAULT_STRIPE_FEE_RATE = 0.022;
const DEFAULT_STRIPE_FIXED_FEE_CENTS = 30;
const DEFAULT_STRIPE_FEE_BUFFER_CENTS = 0;

const parseFeeRate = (value: string | undefined) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed >= 1) {
    return DEFAULT_STRIPE_FEE_RATE;
  }
  return parsed;
};

const parseFixedFee = (value: string | undefined, fallback: number) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.round(parsed);
};

const STRIPE_FEE_RATE = parseFeeRate(
  process.env.NEXT_PUBLIC_STRIPE_DONATION_FEE_RATE,
);
const STRIPE_FIXED_FEE_CENTS = parseFixedFee(
  process.env.NEXT_PUBLIC_STRIPE_DONATION_FIXED_FEE_CENTS,
  DEFAULT_STRIPE_FIXED_FEE_CENTS,
);
const STRIPE_FEE_BUFFER_CENTS = parseFixedFee(
  process.env.NEXT_PUBLIC_STRIPE_DONATION_FEE_BUFFER_CENTS,
  DEFAULT_STRIPE_FEE_BUFFER_CENTS,
);

const calculateGrossChargeToNetAmount = (baseAmountInCents: number) => {
  if (baseAmountInCents <= 0) return 0;
  // Gross-up so net(after Stripe fee) ~= desired base donation.
  const gross = Math.ceil(
    (baseAmountInCents + STRIPE_FIXED_FEE_CENTS) / (1 - STRIPE_FEE_RATE),
  );
  return gross;
};

const calculateProcessingFees = (amountInCents: number) => {
  if (amountInCents <= 0) return 0;
  const grossCharge = calculateGrossChargeToNetAmount(amountInCents);
  const grossUpFee = Math.max(grossCharge - amountInCents, 0);
  return grossUpFee + STRIPE_FEE_BUFFER_CENTS;
};

const parseCustomAmountToCents = (value: string) => {
  if (!value.trim()) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  const cents = Math.round(numericValue * 100);
  if (cents <= 0) return null;
  return cents;
};

interface DonationFormProps {
  prices: any[];
}

export default function DonationForm({ prices }: DonationFormProps) {
  const oneTimePrices = useMemo(
    () =>
      [...prices]
        .filter((p: any) => p.type === "one_time")
        .sort((a: any, b: any) => a.unit_amount - b.unit_amount),
    [prices],
  );

  const initialPrice = oneTimePrices[0] || null;
  const [selected, setSelected] = useState(
    initialPrice ? initialPrice.id : "other",
  );
  const [clientSecret, setClientSecret] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [coverFees, setCoverFees] = useState(true);
  const [baseAmount, setBaseAmount] = useState(initialPrice?.unit_amount || 0);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [isUpdatingIntent, setIsUpdatingIntent] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const cs = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret",
    );

    setClientSecret(cs || "");
  }, []);

  const processingFees = useMemo(
    () => calculateProcessingFees(baseAmount),
    [baseAmount],
  );

  const totalAmount = useMemo(
    () => baseAmount + (coverFees ? processingFees : 0),
    [baseAmount, coverFees, processingFees],
  );

  const isCustomAmountInvalid =
    selected === "other" &&
    (parseCustomAmountToCents(otherAmount) === null ||
      baseAmount < MIN_DONATION_CENTS);

  const isDonationValid = baseAmount >= MIN_DONATION_CENTS;

  const handlePriceSelection = (price: any) => {
    setSelected(price.id);
    setFormError("");
    setBaseAmount(price.unit_amount);
  };

  const handleOtherSelection = () => {
    setSelected("other");
    setFormError("");

    const parsed = parseCustomAmountToCents(otherAmount);
    setBaseAmount(parsed || 0);
  };

  const handleOtherAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOtherAmount(value);

    if (selected !== "other") return;

    const parsed = parseCustomAmountToCents(value);
    setBaseAmount(parsed || 0);
  };

  const updateIntentAmount = async (amount: number) => {
    const res = await fetch("/api/update-payment-intent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientSecret,
        amount,
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || "Unable to update donation amount");
    }
  };

  const handleCoverFeesChecked = async (e: ChangeEvent<HTMLInputElement>) => {
    const nextCoverFees = e.target.checked;
    const previousCoverFees = coverFees;

    setFormError("");
    setCoverFees(nextCoverFees);

    if (!clientSecret) return;

    const nextAmount =
      baseAmount + (nextCoverFees ? calculateProcessingFees(baseAmount) : 0);

    setIsUpdatingIntent(true);
    try {
      await updateIntentAmount(nextAmount);
    } catch (error: any) {
      setCoverFees(previousCoverFees);
      setFormError(error.message || "Unable to update donation amount");
    } finally {
      setIsUpdatingIntent(false);
    }
  };

  const handleBackClick = async () => {
    setFormError("");
    setClientSecret("");
    Router.replace("/donate");
  };

  const handleSubmit = async () => {
    setFormError("");

    if (!isDonationValid) {
      setFormError("Please enter a donation amount of at least $1.00.");
      return;
    }

    setIsCreatingIntent(true);
    try {
      const res = await fetch("/api/create-donation-intent", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalAmount,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(data.error || "Unable to start donation checkout.");
        return;
      }

      setClientSecret(data.clientSecret);
    } catch (error) {
      setFormError("Unable to start donation checkout.");
    } finally {
      setIsCreatingIntent(false);
    }
  };

  return (
    <div className="flex flex-col lg:min-h-screen lg:flex-row w-full justify-center pt-16">
      <div className="mb-10 justify-center items-center lg:flex-1 md:p-10">
        {!clientSecret && (
          <>
            <div className="max-w-[500px] mx-auto pl-4">
              <span className="font-semibold text-primary">
                Select Donation Amount:
              </span>
            </div>
            <div
              className="flex justify-between m-auto p-4"
              style={{ maxWidth: "500px" }}
            >
              {oneTimePrices.map((price: any) => {
                return (
                  <button
                    type="button"
                    key={price.id}
                    onClick={() => handlePriceSelection(price)}
                    className={`rounded-md mb-4 p-3 hover:bg-primary hover:text-white hover:cursor-pointer ${
                      selected === price.id
                        ? "bg-primary text-white"
                        : "bg-white text-black"
                    }`}
                    style={{
                      border: "1px solid rgba(50, 50, 93, 0.1)",
                      boxShadow:
                        "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
                    }}
                  >
                    <h1>${price.unit_amount / 100}</h1>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={handleOtherSelection}
                className={`rounded-md mb-4 p-3 hover:bg-primary hover:text-white hover:cursor-pointer ${
                  selected === "other"
                    ? "bg-primary text-white"
                    : "bg-white text-black"
                }`}
                style={{
                  border: "1px solid rgba(50, 50, 93, 0.1)",
                  boxShadow:
                    "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
                }}
              >
                <h1>Other</h1>
              </button>
            </div>
            {selected === "other" && (
              <div className="w-full max-w-[500px] mx-auto px-4">
                <input
                  id="otherAmount"
                  type="number"
                  min="1"
                  step="0.01"
                  value={otherAmount}
                  placeholder="$0.00"
                  onChange={handleOtherAmountChange}
                  className="rounded-md mb-2 p-3 bg-white w-full"
                  style={{
                    border: "1px solid rgba(50, 50, 93, 0.1)",
                    maxHeight: 44,
                    boxShadow:
                      "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
                  }}
                />
                {isCustomAmountInvalid && (
                  <p className="text-sm text-red-700 mb-2">
                    Enter at least $1.00.
                  </p>
                )}
              </div>
            )}
            {formError && (
              <p className="w-full max-w-[500px] mx-auto px-4 mt-3 text-sm text-red-800">
                {formError}
              </p>
            )}
            <div className="w-full max-w-[500px] mx-auto px-4 mt-4">
              <button
                type="button"
                role="link"
                className="w-full btn"
                onClick={handleSubmit}
                disabled={!isDonationValid || isCreatingIntent}
              >
                {isCreatingIntent
                  ? "Starting checkout..."
                  : "Proceed to Payment Information"}
              </button>
            </div>
          </>
        )}
        {clientSecret && (
          <div>
            <button
              className="flex mb-4 items-center rounded-lg hover:bg-gray-100 p-1 ml-2"
              onClick={handleBackClick}
            >
              <ArrowLeftIcon className="mr-2 w-5 h-5" /> Back
            </button>
            <div className="w-full max-w-[500px] mx-auto px-4 mb-4">
              <div className="rounded-md bg-neutral p-4 text-sm text-primary">
                <div className="flex justify-between mb-1">
                  <span>Donation</span>
                  <span>${(baseAmount / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>
                    Processing fee ({coverFees ? "included" : "not included"})
                  </span>
                  <span>${(processingFees / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 border-t border-primary/20">
                  <span>Total charge</span>
                  <span>${(totalAmount / 100).toFixed(2)}</span>
                </div>
              </div>
            </div>
            {formError && (
              <p className="w-full md:max-w-[500px] mx-auto mb-3 text-sm text-red-800 px-4">
                {formError}
              </p>
            )}
            <Elements options={{ clientSecret }} stripe={stripePromise}>
              <CheckoutForm
                onCoverFeesChecked={handleCoverFeesChecked}
                coverFees={coverFees}
                processingFees={processingFees}
                amount={totalAmount}
                isUpdatingAmount={isUpdatingIntent}
              />
            </Elements>
          </div>
        )}
      </div>
      <div className="flex flex-col lg:flex lg:flex-1 bg-neutral z-0">
        <div className="bg-neutral p-8 lg:min-h-full relative">
          <div className="flex flex-col items-center lg:sticky lg:top-[20%]">
            <h1 className="text-primary font-bold text-3xl mb-4 text-center">
              A Gift that Give Back
            </h1>
            <h2 className="text-gray-800 text-xl mb-4 text-center">
              Donate Today in Order to Make an Impact on Supporting Breast
              Cancer Clinical Trials, Education and Patient Care
            </h2>
            <div>
              <div className="mt-20 mb-4 text-gray-600">
                <h4 className="font-semibold">
                  This is what your donation does:
                </h4>
              </div>
              <ul className="list-disc list-outside ml-4">
                <li className="mb-2">
                  Assists breast cancer clinical trial patients with the added
                  cost for transportation, meals and child care (Patients may
                  have extra visits to carefully monitor the side effects and
                  safety while participating in a clinical trial).
                </li>
                <li className="mb-2">
                  Provide educational opportunities for breast cancer physicians
                  to increase their knowledge on current clinical trials and
                  research by attending a conference or symposium.
                </li>
                <li className="mb-2">
                  Educate the general public (corporations, community groups and
                  college students) on breast cancer prevention, detection,
                  treatment and clinical trials.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
